import { useEffect, useMemo, useRef, useState } from "react";
import { CircleCheck, Clock3, DatabaseZap, RefreshCw, RotateCcw } from "lucide-react";
import { ApiError, fetchSalesSnapshot, fetchSalesStatusVersion, fetchSalesStatuses, getSession, logout, saveSalesStatus } from "./api";
import { AppShell, type AppSection } from "./components/AppShell";
import { EmptyState } from "./components/EmptyState";
import { ErrorState } from "./components/ErrorState";
import { LoginScreen } from "./components/LoginScreen";
import { Pagination } from "./components/Pagination";
import { SalesSummary } from "./components/SalesSummary";
import { SalesTable } from "./components/SalesTable";
import { SourcesInsights } from "./components/SourcesInsights";
import { MarketValueDashboard } from "./components/MarketValueDashboard";
import { LeadCheckerDashboard } from './components/LeadCheckerDashboard';
import { CatalogDashboard } from "./components/CatalogDashboard";
import { PlannedSection, type PlannedKey } from "./components/PlannedSection";
import { CONDITION_VALUES, type AccessContext, type ConditionFilter, type PriceScopeFilter, type PriceTypeFilter, type SalesCatalogSnapshot, type SalesFiltersState, type SalesListing, type SalesPeriod, type SalesSort, type SalesStatus, type SortDirection } from "./types";
import { countSalesPeriods, selectSales } from './sales-snapshot';

const PRICE_TYPES: PriceTypeFilter[] = ["fixed", "negotiable", "unknown"];
const PRICE_SCOPES: PriceScopeFilter[] = ["item", "lot"];
const DEFAULT_CONDITIONS: ConditionFilter[] = CONDITION_VALUES.filter(value => value !== "used" && value !== "for_parts");

const defaults: SalesFiltersState = {
  q: "",
  period: "day",
  from: "",
  to: "",
  sources: null,
  priceTypes: null,
  priceScopes: null,
  conditions: DEFAULT_CONDITIONS,
  priceView: "regular",
  scoreBands: null,
  sort: "profitability",
  direction: "asc",
  page: 1,
  pageSize: 25
};

function sameSelection<T extends string>(left: T[] | null, right: T[] | null) {
  if (left === null || right === null) return left === right;
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

function localToday() {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: "Europe/Chisinau",
    year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date());
  const value = (part: string) => parts.find(item => item.type === part)?.value || "";
  return `${value("year")}-${value("month")}-${value("day")}`;
}

function localYesterday() {
  const [year, month, day] = localToday().split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day - 1)).toISOString().slice(0, 10);
}

function validDate(value: string | null) {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = Date.parse(`${value}T12:00:00Z`);
  return !Number.isNaN(parsed) && new Date(parsed).toISOString().slice(0, 10) === value;
}

export function filtersFromSearch(search: string): SalesFiltersState {
  const query = new URLSearchParams(search);
  const priceView = query.get("priceView") === "off50" ? "off50" : "regular";
  const sort = query.get("sort");
  const direction = query.get("direction");
  const requestedPeriod = query.get("period");
  const period: SalesPeriod = (["day", "yesterday", "week", "range", "all"] as string[]).includes(requestedPeriod || "")
    ? requestedPeriod as SalesPeriod
    : query.get("recent") === "1" ? "week" : query.get("recent") === "0" ? "all" : priceView === "off50" ? "all" : defaults.period;
  const today = period === "range" ? localToday() : "";
  const from = period === "range" && validDate(query.get("from")) ? query.get("from")! : today;
  const to = period === "range" && validDate(query.get("to")) ? query.get("to")! : from;
  const pageSize = Number(query.get("pageSize"));
  const page = Number(query.get("page"));
  const readMulti = <T extends string>(name: string, allowed?: readonly T[], fallback: T[] | null = null): T[] | null => {
    if (!query.has(name)) return fallback === null ? null : [...fallback];
    if (query.get(name) === "*") return null;
    const values = [...new Set((query.get(name) || "").split(",").map(value => value.trim()).filter(Boolean))];
    return (allowed ? values.filter((value): value is T => allowed.includes(value as T)) : values as T[]);
  };
  return {
    q: query.get("q") ?? "",
    period, from: from <= to ? from : to, to: from <= to ? to : from,
    sources: readMulti<string>("sources", undefined, defaults.sources),
    priceTypes: readMulti<PriceTypeFilter>("priceTypes", PRICE_TYPES),
    priceScopes: readMulti<PriceScopeFilter>("priceScopes", PRICE_SCOPES),
    conditions: readMulti<ConditionFilter>("conditions", CONDITION_VALUES, priceView === "off50" ? null : defaults.conditions),
    priceView,
    scoreBands: null,
    sort: (["score", "profitability", "condition", "added", "source", "title"] as string[]).includes(sort ?? "") ? sort as SalesSort : defaults.sort,
    direction: direction === "asc" || direction === "desc" ? direction as SortDirection : defaults.direction,
    page: Number.isInteger(page) && page > 0 ? page : 1,
    pageSize: pageSize === 50 || pageSize === 100 ? pageSize : 25
  };
}

function initialFilters(): SalesFiltersState {
  return filtersFromSearch(window.location.search);
}

function syncUrl(filters: SalesFiltersState) {
  const query = new URLSearchParams();
  const writeMulti = <T extends string>(name: string, value: T[] | null, fallback: T[] | null) => {
    if (sameSelection(value, fallback)) return;
    query.set(name, value === null ? "*" : value.join(","));
  };
  if (filters.q.trim()) query.set("q", filters.q.trim());
  if (filters.priceView === "off50") query.set("priceView", "off50");
  if (filters.period !== defaults.period) query.set("period", filters.period);
  if (filters.period === "range") { query.set("from", filters.from); query.set("to", filters.to); }
  writeMulti("sources", filters.sources, defaults.sources);
  writeMulti("priceTypes", filters.priceTypes, defaults.priceTypes);
  writeMulti("priceScopes", filters.priceScopes, defaults.priceScopes);
  writeMulti("conditions", filters.conditions, defaults.conditions);
  if (filters.sort !== defaults.sort) {
    query.set("sort", filters.sort);
    query.set("direction", filters.direction);
  } else if (filters.direction !== defaults.direction) {
    query.set("direction", filters.direction);
  }
  if (filters.page !== 1) query.set("page", String(filters.page));
  if (filters.pageSize !== defaults.pageSize) query.set("pageSize", String(filters.pageSize));
  const next = query.size ? `${window.location.pathname}?${query}` : window.location.pathname;
  window.history.replaceState(null, "", next);
}

export function App() {
  const [access, setAccess] = useState<AccessContext | null>();

  useEffect(() => {
    void getSession()
      .then(session => setAccess(session.authenticated ? session : null))
      .catch(() => setAccess(null));
  }, []);

  if (access === undefined) return <LoginScreen loading onAuthenticated={setAccess} />;
  if (access === null) return <LoginScreen onAuthenticated={setAccess} />;

  const signOut = async () => {
    try { await logout(); } finally { setAccess(null); }
  };

  return <WorkspaceApplication access={access} onLogout={() => void signOut()} />;
}

function sectionFromPath(): AppSection {
  if (window.location.pathname === "/catalog" || window.location.pathname.startsWith("/catalog/")) return "catalog";
  if (window.location.pathname === "/deals") return "deals";
  if (window.location.pathname === "/sources" || window.location.pathname.startsWith("/sources/")) return "sources";
  if (window.location.pathname === "/market-value" || window.location.pathname.startsWith("/market-value/")) return "market-value";
  if (window.location.pathname === '/lead-checker') return 'lead-checker';
  if (window.location.pathname === "/quality") return "quality";
  if (window.location.pathname === "/price-history") return "price-history";
  if (window.location.pathname === "/inventory") return "inventory";
  return "sales";
}

function WorkspaceApplication({ access, onLogout }: { access: AccessContext; onLogout: () => void }) {
  const [section, setSection] = useState<AppSection>(sectionFromPath);

  useEffect(() => {
    const permission = { sales: "sales:read", catalog: "catalog:read", deals: "deals:read", sources: "sources:read", "market-value": "market:read", "lead-checker": 'lead:read', quality: "quality:read", "price-history": "price-history:read", inventory: "inventory:read" }[section];
    if (!access.permissions.includes(permission)) {
      window.history.replaceState(null, "", "/");
      setSection("sales");
    }
  }, [section, access.permissions]);

  useEffect(() => {
    const handlePopState = () => setSection(sectionFromPath());
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const navigate = (nextSection: AppSection, href: string) => {
    if (`${window.location.pathname}${window.location.search}` !== href) window.history.pushState(null, "", href);
    setSection(nextSection);
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  return <AppShell access={access} section={section} onNavigate={navigate} onLogout={onLogout}>
    {section === "catalog" && access.permissions.includes("catalog:read") ? <CatalogDashboard onUnauthorized={onLogout} />
      : (["deals", "quality", "price-history", "inventory"] as string[]).includes(section) && access.permissions.includes({ deals: "deals:read", quality: "quality:read", "price-history": "price-history:read", inventory: "inventory:read" }[section as PlannedKey])
        ? <PlannedSection section={section as PlannedKey} />
      : section === "sources" && access.permissions.includes("sources:read") ? <SourcesInsights onUnauthorized={onLogout} />
      : section === "market-value" && access.permissions.includes("market:read")
        ? <MarketValueDashboard onUnauthorized={onLogout} />
      : section === 'lead-checker' && access.permissions.includes('lead:read')
        ? <LeadCheckerDashboard onUnauthorized={onLogout} />
        : <SalesApplication onLogout={onLogout} />}
  </AppShell>;
}

function SalesApplication({ onLogout }: { onLogout: () => void }) {
  const [filters, setFilters] = useState<SalesFiltersState>(initialFilters);
  const [snapshot, setSnapshot] = useState<SalesCatalogSnapshot | null>(null);
  const [statuses, setStatuses] = useState<ReadonlyMap<string, SalesStatus>>(new Map());
  const [statusError, setStatusError] = useState('');
  const statusRevision = useRef<string | null | undefined>(undefined);
  const [clock, setClock] = useState(() => new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const [statusUpdates, setStatusUpdates] = useState<Record<string, SalesStatus>>({});
  const [statusErrors, setStatusErrors] = useState<Record<string, string>>({});
  const data = useMemo(() => snapshot ? selectSales(snapshot, filters, statuses, clock) : null,
    [snapshot, filters, statuses, clock]);
  const periodCounts = useMemo(() => snapshot ? countSalesPeriods(snapshot, filters, clock) : null,
    [snapshot, filters, clock]);
  const changeStatus = async (item: SalesListing, status: SalesStatus) => {
    setStatusErrors(current => { const next = { ...current }; delete next[item.key]; return next; });
    setStatusUpdates(current => ({ ...current, [item.key]: status }));
    try {
      await saveSalesStatus(item.sourceDatabase, item.listingId, status);
      setStatuses(current => new Map(current).set(item.key, status));
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onLogout();
      setStatusErrors(current => ({ ...current, [item.key]: caught instanceof Error ? caught.message : "Ошибка сохранения" }));
    } finally {
      setStatusUpdates(current => { const next = { ...current }; delete next[item.key]; return next; });
    }
  };
  useEffect(() => {
    let displayedDay = localToday();
    const checkDay = () => {
      setClock(new Date());
      const today = localToday();
      if (today === displayedDay) return;
      displayedDay = today;
      setFilters(current => current.period === 'day' || current.period === 'yesterday' ? { ...current, page: 1 } : current);
    };
    const timer = window.setInterval(checkDay, 60_000);
    document.addEventListener("visibilitychange", checkDay);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", checkDay);
    };
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        setSnapshot(await fetchSalesSnapshot(controller.signal, revision > 0));
      } catch (caught) {
        if (!controller.signal.aborted) {
          if (caught instanceof ApiError && caught.status === 401) onLogout();
          else {
            setSnapshot(null);
            setError(caught instanceof Error ? caught.message : "Неизвестная ошибка");
          }
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    void load();
    return () => {
      controller.abort();
    };
  }, [revision, onLogout]);

  useEffect(() => {
    const controller = new AbortController();
    const refreshStatuses = async () => {
      try {
        const response = await fetchSalesStatuses(controller.signal);
        if (!controller.signal.aborted) {
          setStatuses(new Map(response.items.map(row => [`${row.sourceDatabase}:${row.listingId}`, row.status])));
          statusRevision.current = response.revision;
          setStatusError('');
        }
      } catch (caught) {
        if (controller.signal.aborted) return;
        if (caught instanceof ApiError && caught.status === 401) onLogout();
        else setStatusError('Статусы сейчас недоступны. Выбор статуса временно отключён.');
      }
    };
    void refreshStatuses();
    const checkStatuses = async () => {
      try {
        const current = await fetchSalesStatusVersion(controller.signal);
        if (controller.signal.aborted) return;
        if (statusRevision.current !== current.revision) await refreshStatuses();
        else setStatusError('');
      } catch (caught) {
        if (controller.signal.aborted) return;
        if (caught instanceof ApiError && caught.status === 401) onLogout();
        else setStatusError('Статусы сейчас недоступны. Выбор статуса временно отключён.');
      }
    };
    const timer = window.setInterval(() => void checkStatuses(), 120_000);
    const onVisible = () => { if (document.visibilityState === 'visible') void checkStatuses(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { controller.abort(); window.clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); };
  }, [onLogout]);

  useEffect(() => { syncUrl(filters); }, [filters]);
  useEffect(() => {
    if (!data) return;
    const lastPage = Math.max(1, Math.ceil(data.total / data.pageSize));
    if (filters.page > lastPage) setFilters(current => ({ ...current, page: lastPage }));
  }, [data, filters.page]);

  const filtered = useMemo(
    () => Boolean(filters.q || filters.priceView !== defaults.priceView || filters.period !== defaults.period || filters.sources !== null || filters.priceTypes !== null || filters.priceScopes !== null || filters.conditions !== null),
    [filters]
  );
  const patchFilters = (patch: Partial<SalesFiltersState>) => setFilters(current => ({ ...current, ...patch }));
  const showPeriod = (period: "day" | "yesterday" | "week" | "all") => {
    patchFilters({ period, page: 1 });
    window.requestAnimationFrame(() => document.getElementById("sales-list")?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };
  const syncLabel = data?.sync?.completedAt
    ? new Intl.DateTimeFormat("ru-RU", { dateStyle: "short", timeStyle: "short" }).format(new Date(data.sync.completedAt))
    : null;

  return <>
    <div className="page-heading">
      <div>
        <span className="eyebrow">Объявления площадок</span>
        <h1>Каталог объявлений</h1>
        <p>{data ? `${data.summary.total.toLocaleString("ru-RU")} объявлений в облаке. ${filters.period === "day" ? `На экране — находки за сегодня, ${localToday()}.` : filters.period === "yesterday" ? `На экране — находки за вчера, ${localYesterday()}.` : filters.period === "week" ? "На экране — находки за последние 7 дней." : filters.period === "range" ? `На экране — находки с ${filters.from} по ${filters.to}.` : "Показаны объявления по выбранным фильтрам."}` : "Подготовленные совпадения из единой базы объявлений."}</p>
      </div>
      <button className="refresh-button" type="button" onClick={() => setRevision(value => value + 1)} disabled={loading}>
        <RefreshCw className={loading ? "spin" : ""} size={16} />Обновить
      </button>
    </div>

    <SalesSummary
      counts={periodCounts}
      activePeriod={filters.period}
      onSelectPeriod={showPeriod}
    />

    <section className="list-panel" id="sales-list">
      <div className="list-panel-heading sales-list-heading">
        <div><DatabaseZap size={18} /><span><strong>Объявления</strong><small>{data ? `${data.total.toLocaleString("ru-RU")} найдено` : "Чтение базы"}</small></span></div>
        <div className="sales-list-meta">
          {data?.sync && <span className={`sync-badge ${syncLabel ? "is-current" : "is-stale"}`} title={`Строк в серверной копии: ${data.sync.rowCount.toLocaleString("ru-RU")}`}>
            {syncLabel ? <CircleCheck size={13} /> : <Clock3 size={13} />}
            {syncLabel ? `Обновлено ${syncLabel}` : "Синхронизация не выполнялась"}
          </span>}
          <span className="read-only-badge">ДАННЫЕ ПОИСКА · ТОЛЬКО ЧТЕНИЕ</span>
        </div>
      </div>
      <div className="sales-list-toolbar">
        <div className="sales-toolbar-group"><span>Период</span><div className="sales-quick-sort" aria-label="Период объявлений"><button className={filters.period === "day" ? "active" : ""} type="button" onClick={() => patchFilters({ period: "day", page: 1 })}>За сегодня</button><button className={filters.period === "yesterday" ? "active" : ""} type="button" onClick={() => patchFilters({ period: "yesterday", page: 1 })}>За вчера</button><button className={filters.period === "week" ? "active" : ""} type="button" onClick={() => patchFilters({ period: "week", page: 1 })}>За 7 дней</button><button className={filters.period === "range" ? "active" : ""} type="button" onClick={() => patchFilters({ period: "range", from: filters.from || localToday(), to: filters.to || localToday(), page: 1 })}>Выбрать даты</button><button className={filters.period === "all" ? "active" : ""} type="button" onClick={() => patchFilters({ period: "all", page: 1 })}>Все даты</button></div></div>
        {filters.period === "range" && <div className="sales-toolbar-group sales-date-range"><label>С <input aria-label="Дата начала" type="date" value={filters.from} onChange={event => { const from = event.target.value || filters.from; patchFilters({ from, to: from > filters.to ? from : filters.to, page: 1 }); }} /></label><label>По <input aria-label="Дата окончания" type="date" value={filters.to} onChange={event => { const to = event.target.value || filters.to; patchFilters({ from: to < filters.from ? to : filters.from, to, page: 1 }); }} /></label></div>}
        <div className="sales-toolbar-group"><span>Порядок</span><div className="sales-quick-sort" aria-label="Порядок объявлений"><button className={filters.sort === 'added' && filters.direction === 'desc' ? 'active' : ''} type="button" onClick={() => patchFilters({ sort: 'added', direction: 'desc', page: 1 })}>Новые</button><button className={filters.sort === 'profitability' && filters.direction === 'asc' ? 'active' : ''} type="button" onClick={() => patchFilters({ sort: 'profitability', direction: 'asc', page: 1 })}>Выгодные</button></div></div>
        {filtered && <button className="inline-reset-button" type="button" onClick={() => setFilters(defaults)}><RotateCcw size={13} />Сбросить фильтры</button>}
      </div>

      {error
        ? <ErrorState message={error} onRetry={() => setRevision(value => value + 1)} />
        : data
          ? <>{statusError && <div className="sales-status-warning" role="alert">{statusError}</div>}<SalesTable
              items={data.items}
              filters={filters}
              filterOptions={data.filters}
              busy={loading}
              onChange={patchFilters}
              onStatusChange={changeStatus}
              statusUpdates={statusUpdates}
              statusErrors={statusErrors}
              statusDisabled={Boolean(statusError)}
            />{data.items.length
              ? <Pagination
                  page={filters.page}
                  pageSize={filters.pageSize}
                  total={data.total}
                  onPage={page => patchFilters({ page })}
                  onPageSize={pageSize => patchFilters({ pageSize, page: 1 })}
                />
              : !loading && <EmptyState filtered={filtered} />}</>
          : loading
            ? <div className="loading-table" aria-label="Загрузка"><span /><span /><span /><span /><span /></div>
            : <EmptyState filtered={filtered} />}
    </section>
  </>;
}
